import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Clock, Users, FolderKanban, FileText, GanttChart, Copy, Check, ChevronDown, Sparkles, Plus, X, Trash2, Loader2 } from 'lucide-react';
import { toast } from '../lib/toast';

interface PromptCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  prompts: string[];
}

interface CustomPrompt {
  id: string;
  category_id: string;
  title: string;
  content: string;
  created_at: string;
}

const categories: PromptCategory[] = [
  {
    id: 'task-management',
    title: 'Task & Time Management',
    icon: <Clock className="h-6 w-6 text-indigo-500" />,
    description: 'Optimize task prioritization and time allocation',
    prompts: [
      "Rank these tasks based on urgency and deadlines: {task_list}",
      "Suggest a daily work schedule based on these tasks and priorities: {task_list}",
      "Analyze my team's task completion time and suggest productivity improvements: {work_log}",
      "Break down this complex task into smaller, manageable steps: {task_description}",
      "Suggest an optimal time-blocking strategy for my daily schedule: {tasks}, {meeting_times}"
    ]
  },
  {
    id: 'team-collaboration',
    title: 'Team Collaboration & Resource Allocation',
    icon: <Users className="h-6 w-6 text-blue-500" />,
    description: 'Enhance team coordination and resource management',
    prompts: [
      "Assign the best team members for this task based on skills and availability: {task_description}, {team_data}",
      "Generate a conflict resolution strategy for this team issue: {conflict_details}",
      "Summarize this meeting and extract key action items: {meeting_notes}",
      "Suggest communication improvements based on our team's Slack/email activity: {team_chat_logs}",
      "Provide a framework for effective 1-on-1 meetings between managers and employees"
    ]
  },
  {
    id: 'project-planning',
    title: 'Project Planning & Execution',
    icon: <FolderKanban className="h-6 w-6 text-green-500" />,
    description: 'Streamline project planning and implementation',
    prompts: [
      "Generate a step-by-step project plan for this goal: {project_goal}",
      "Identify potential risks in this project and suggest mitigation strategies: {project_details}",
      "Create a detailed roadmap for a {industry}-related project with milestones and deadlines",
      "Suggest KPIs to track the success of this project: {project_type}",
      "Optimize resource allocation for this project to improve efficiency and budget utilization: {resource_list}"
    ]
  },
  {
    id: 'documentation',
    title: 'AI-Assisted Documentation & Reports',
    icon: <FileText className="h-6 w-6 text-purple-500" />,
    description: 'Generate professional documentation and reports',
    prompts: [
      "Draft a professional project proposal based on these requirements: {project_requirements}",
      "Summarize the key learnings and outcomes from this completed project: {project_summary}",
      "Generate a progress report for stakeholders based on current project data: {progress_data}",
      "Create a persuasive email update for clients about project status: {project_status}",
      "Write a post-mortem analysis identifying what worked and what didn't in this project: {project_name}"
    ]
  },
  {
    id: 'gantt-charts',
    title: 'Automated Gantt Charts & Visual Project Mapping',
    icon: <GanttChart className="h-6 w-6 text-orange-500" />,
    description: 'Create visual project timelines and dependencies',
    prompts: [
      "Generate a Gantt chart based on these tasks, dependencies, and deadlines: {task_list}, {dependencies}",
      "Create a mind map for this project to visualize goals, tasks, and deliverables: {project_details}",
      "Provide an AI-generated timeline for completing this project, considering team capacity: {project_scope}",
      "Suggest sprint planning ideas based on our backlog and team velocity: {backlog_items}, {team_velocity}",
      "Analyze past project timelines and predict realistic deadlines for this new project: {historical_data}"
    ]
  },
  {
    id: 'others',
    title: 'Custom Prompts',
    icon: <Sparkles className="h-6 w-6 text-pink-500" />,
    description: 'Your personalized collection of custom prompts',
    prompts: []
  }
];

export default function Prompts({ navigate }: { navigate: (to: string) => void }) {
  const [copiedPrompt, setCopiedPrompt] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showAddPromptModal, setShowAddPromptModal] = useState(false);
  const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [newPromptTitle, setNewPromptTitle] = useState('');
  const [newPromptContent, setNewPromptContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCustomPrompts();
  }, []);

  const fetchCustomPrompts = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('custom_prompts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCustomPrompts(data || []);
    } catch (error) {
      console.error('Error fetching custom prompts:', error);
      toast.error('Failed to load custom prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(prompt);
    toast.success('Prompt copied to clipboard');
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const handleDeletePrompt = async (promptId: string) => {
    try {
      const { error } = await supabase
        .from('custom_prompts')
        .delete()
        .eq('id', promptId);

      if (error) throw error;
      
      setCustomPrompts(prev => prev.filter(p => p.id !== promptId));
      toast.success('Prompt deleted successfully');
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast.error('Failed to delete prompt');
    }
  };

  const handleAddPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('custom_prompts')
        .insert({
          user_id: user.id,
          category_id: 'others',
          title: newPromptTitle,
          content: newPromptContent,
          created_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (error) throw error;

      setCustomPrompts(prev => [data, ...prev]);
      setShowAddPromptModal(false);
      setNewPromptTitle('');
      setNewPromptContent('');
      toast.success('Custom prompt added successfully');
    } catch (error) {
      console.error('Error adding prompt:', error);
      toast.error('Failed to add custom prompt');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Prompts Library</h1>
            <p className="mt-4 mb-6 text-lg text-gray-600 dark:text-gray-400">
              Explore our curated collection of AI prompts to enhance your project management workflow
            </p>
            <button
              onClick={() => setShowAddPromptModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Custom Prompt
            </button>
          </div>

          <div className="space-y-8">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-left">
                    {category.icon}
                    <div className="text-left">
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {category.title}
                      </h2>
                      <p className="mt-1 text-gray-500 dark:text-gray-400 text-left">
                        {category.description}
                      </p>
                    </div>
                    </div>
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                        expandedCategories.has(category.id) ? 'transform rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>
                {/* Prompts List */}
                <div 
                  className={`divide-y divide-gray-200 dark:divide-gray-700 transition-all duration-200 ${
                    expandedCategories.has(category.id) ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                  }`}
                >
                  {/* Show built-in prompts */}
                  {category.prompts.map((prompt, index) => (
                    <div
                      key={index}
                      className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                    >
                      <div className="flex items-start justify-between">
                        <p className="text-gray-900 dark:text-white font-medium">
                          {prompt}
                        </p>
                        <button
                          onClick={() => handleCopyPrompt(prompt)}
                          className="ml-4 p-2 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          title="Copy prompt"
                        >
                          {copiedPrompt === prompt ? (
                            <Check className="h-5 w-5 text-green-500" />
                          ) : (
                            <Copy className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200">
                          {category.title}
                        </span>
                      </div>
                    </div>
                  ))}
                  {/* Show custom prompts for 'others' category */}
                  {category.id === 'others' && customPrompts.map((prompt) => (
                    <div
                      key={prompt.id}
                      className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                            {prompt.title}
                          </h4>
                          <p className="text-gray-900 dark:text-white">
                            {prompt.content}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleCopyPrompt(prompt.content)}
                            className="p-2 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Copy prompt"
                          >
                            {copiedPrompt === prompt.content ? (
                              <Check className="h-5 w-5 text-green-500" />
                            ) : (
                              <Copy className="h-5 w-5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleDeletePrompt(prompt.id)}
                            className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Delete prompt"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Add Custom Prompt Modal */}
        {showAddPromptModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Create Custom Prompt
                  </h2>
                  <button
                    onClick={() => setShowAddPromptModal(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <form onSubmit={handleAddPrompt} className="space-y-4">
                  <div>
                    <label htmlFor="prompt-title" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      id="prompt-title"
                      value={newPromptTitle}
                      onChange={(e) => setNewPromptTitle(e.target.value)}
                      placeholder="Enter a descriptive title for your prompt"
                      className="w-full rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="prompt-content" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Prompt Content
                    </label>
                    <textarea
                      id="prompt-content"
                      value={newPromptContent}
                      onChange={(e) => setNewPromptContent(e.target.value)}
                      placeholder="Enter your custom prompt..."
                      rows={4}
                      className="w-full rounded-md"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAddPromptModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Prompt'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}